import React, { useEffect, useRef, useState } from "react";

interface Props {
    options: string[];
    values: string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
}

/** Multi-choice picker as a dropdown: a select-look toggle showing the current
 *  selection ("Any" when empty) that opens a panel of checkboxes. */
const MultiSelectDropdown: React.FC<Props> = ({ options, values, onChange, disabled }) => {
    const [open, setOpen] = useState(false);
    // Open upward when there isn't room below (e.g. the last filter on the page).
    const [openUp, setOpenUp] = useState(false);
    const boxRef = useRef<HTMLDivElement>(null);

    const toggleOpen = () => {
        if (!open && boxRef.current) {
            const r = boxRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - r.bottom;
            // ~panel max-height (14rem) + margin
            setOpenUp(spaceBelow < 250 && r.top > spaceBelow);
        }
        setOpen((o) => !o);
    };

    // Close the dropdown on any click outside it.
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    const toggle = (opt: string) => {
        onChange(
            values.includes(opt)
                ? values.filter((v) => v !== opt)
                : [...values, opt]
        );
    };

    const summary =
        values.length === 0
            ? "Select"
            : `${values.length} selected`;

    return (
        <div ref={boxRef} className="bh-multiselect">
            <button
                type="button"
                className="bh-multiselect-toggle"
                onClick={toggleOpen}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="bh-multiselect-summary">{summary}</span>
                <span aria-hidden="true">{open ? "▴" : "▾"}</span>
            </button>

            {open && (
                <div
                    className={`bh-multiselect-panel${openUp ? " bh-multiselect-panel--up" : ""}`}
                    role="listbox"
                    aria-multiselectable="true"
                >
                    {options.map((opt) => (
                        <label key={opt}>
                            <input
                                type="checkbox"
                                checked={values.includes(opt)}
                                onChange={() => toggle(opt)}
                            />
                            {opt}
                        </label>
                    ))}
                </div>
            )}

            {/* Selected values as removable chips under the box */}
            {values.length > 0 && (
                <div className="bh-multiselect-chips">
                    {values.map((v) => (
                        <span key={v} className="bh-multiselect-chip">
                            {v}
                            <button
                                type="button"
                                onClick={() => toggle(v)}
                                disabled={disabled}
                                aria-label={`Remove ${v}`}
                            >
                                ✕
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;